import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { createPortal } from "react-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  ThemeProvider,
  BaseStyles,
  TextInput,
  FormControl,
  Checkbox,
  CheckboxGroup,
} from "@primer/react";
import { SearchIcon } from "@primer/octicons-react";
import { handleEventClick } from "./calendarTooltip";
import {
  StatusCheckboxes,
  computeOperatingHours,
  filterEventsByStatus,
  statusValues,
} from "./facilityFilters";

// Booking confirmation and blocked dates are drawn on top of the request-status
// colours, so they get their own swatches rather than a competing status list.
const OVERLAY_LEGEND = [
  { label: "Confirmed Booking", className: "fc-event--booking-confirmed" },
  { label: "Blocked Date", className: "fc-event--blocked-legend" },
];

function OverlayLegend() {
  return (
    <div>
      <div className="text-bold f6 mb-2">Legend</div>
      <div className="d-flex flex-column gap-1">
        {OVERLAY_LEGEND.map((item) => (
          <span key={item.className} className="d-flex flex-items-center gap-2 f6 color-fg-muted">
            <span className={`calendar-legend-swatch ${item.className}`} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function FacilityCalendarFilters({ filters, onChange, requestStatuses }) {
  const debouncedSearch = useDebounce(
    useCallback((value) => onChange((prev) => ({ ...prev, search: value })), [onChange]),
    300
  );

  return (
    <ThemeProvider>
      <BaseStyles style={{ background: "transparent" }}>
        <div className="d-flex flex-column" style={{ gap: "16px" }}>
          <FormControl>
            <FormControl.Label>Search</FormControl.Label>
            <TextInput
              type="search"
              placeholder="Search by title..."
              defaultValue={filters.search}
              onChange={(e) => debouncedSearch(e.target.value)}
              leadingVisual={SearchIcon}
              block
              size="large"
            />
          </FormControl>

          <StatusCheckboxes
            statuses={requestStatuses}
            selected={filters.statuses}
            onChange={(statuses) => onChange((prev) => ({ ...prev, statuses }))}
          />

          <CheckboxGroup>
            <CheckboxGroup.Label>Display</CheckboxGroup.Label>
            <FormControl>
              <Checkbox
                checked={filters.weekends}
                onChange={(e) => onChange((prev) => ({ ...prev, weekends: e.target.checked }))}
              />
              <FormControl.Label>Show Weekends</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.businessHours}
                onChange={(e) => onChange((prev) => ({ ...prev, businessHours: e.target.checked }))}
              />
              <FormControl.Label>Show Business Hours</FormControl.Label>
            </FormControl>
            <FormControl>
              <Checkbox
                checked={filters.includeArchived}
                onChange={(e) => onChange((prev) => ({ ...prev, includeArchived: e.target.checked }))}
              />
              <FormControl.Label>Show Archived / Cancelled</FormControl.Label>
              <FormControl.Caption>Bookings on archived events and cancelled bookings, which no longer hold their facilities.</FormControl.Caption>
            </FormControl>
          </CheckboxGroup>

          <OverlayLegend />
        </div>
      </BaseStyles>
    </ThemeProvider>
  );
}

export function VenueCalendar({ eventsUrl, timezone, facility, requestStatuses = [] }) {
  const calendarRef = useRef(null);
  const [filters, setFilters] = useState({
    search: "",
    weekends: true,
    businessHours: true,
    includeArchived: false,
    statuses: statusValues(requestStatuses),
  });

  const { earliestOpen, latestClose, businessHours } = computeOperatingHours([facility]);

  const fetchEvents = useCallback(
    (fetchInfo, successCallback, failureCallback) => {
      const url = new URL(eventsUrl, window.location.origin);
      url.searchParams.set("start", fetchInfo.startStr);
      url.searchParams.set("end", fetchInfo.endStr);
      if (filters.search) url.searchParams.set("search", filters.search);
      // GR-4399: bookings on archived events and cancelled bookings are left off unless asked for.
      if (filters.includeArchived) url.searchParams.set("include_archived", "true");

      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((events) => successCallback(filterEventsByStatus(events, filters.statuses, requestStatuses)))
        .catch(failureCallback);
    },
    [eventsUrl, filters, requestStatuses]
  );

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) api.refetchEvents();
  }, [filters.statuses, filters.includeArchived]);

  const filtersPortal = document.getElementById("venue-filters-portal");

  return (
    <div className="admin-events-calendar">
      {filtersPortal &&
        createPortal(
          <FacilityCalendarFilters filters={filters} onChange={setFilters} requestStatuses={requestStatuses} />,
          filtersPortal
        )}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        buttonText={{
          today: "Today",
          month: "Month",
          week: "Week",
          day: "Day",
        }}
        events={fetchEvents}
        eventClick={handleEventClick}
        timeZone={timezone || "local"}
        height="auto"
        weekends={filters.weekends}
        slotMinTime={earliestOpen}
        slotMaxTime={latestClose}
        businessHours={filters.businessHours ? businessHours : false}
        nowIndicator
        dayMaxEvents={4}
        eventDisplay="block"
      />
    </div>
  );
}
